/*
 * BNO central overlay-state controller (site-wide, one instance).
 *
 * Single source of truth for "is any overlay open?". It is derived PURELY from
 * the live DOM — there is no counter and no per-overlay register/open/close API,
 * so the state cannot drift and swapping straight from one overlay to another (or
 * closing an inner overlay while an outer one stays open) is handled for free.
 *
 * One MutationObserver watches every signal the theme's overlays already expose,
 * and one derivation function (anyOverlayOpen) recomputes the answer from the DOM:
 *
 *   - `dialog[open]` .................. cart drawer, search, quick-add, popup-link,
 *                                       bundle "learn more", image zoom
 *                                       (Horizon DialogComponent — showModal()/show())
 *   - `html[scroll-lock]` ............. the hamburger <details> menu (set via the
 *                                       toggle listener in dialog.js)
 *   - `.localization-wrapper:not([hidden])` .. the currency / country popup
 *   - `.bno-buybar__dropdown:not([hidden])` .. the buy-bar plugin switcher
 *
 * When any signal is present it adds `bno-drawer-open` to <html>; when the last
 * one clears it removes it, and dispatches `bno:overlay-change` on <html> so
 * consumers (e.g. the cookie banner's a11y sequencing) can react without
 * re-deriving. Everything user-facing keys off the class in CSS:
 *   - the buy bar hides            (sections/bno-plugin-buy-bar.liquid)
 *   - the cookie banner hides      (assets/bno-shared.css)
 *
 * The flag is a visibility concern only — it does NOT scroll-lock the page.
 * Overlays that need a scroll lock still own it themselves (DialogComponent's
 * body{position:fixed}; the <details> menu's html[scroll-lock]); the two
 * lightweight header dropdowns deliberately do not lock.
 *
 * This file OWNS the `bno-drawer-open` class. No other file should add or remove
 * it — bno-cookie-consent.js now only consumes it.
 */
(function () {
  var docEl = document.documentElement;

  /* Presence of any of these (or the scroll-lock attribute below) = open. */
  var OPEN_SELECTORS = [
    'dialog[open]',
    'dropdown-localization-component .localization-wrapper:not([hidden])',
    '.bno-buybar__dropdown:not([hidden])',
  ];

  function anyOverlayOpen() {
    if (docEl.hasAttribute('scroll-lock')) return true;
    for (var i = 0; i < OPEN_SELECTORS.length; i++) {
      if (document.querySelector(OPEN_SELECTORS[i]) != null) return true;
    }
    return false;
  }

  var isOpen = false;

  function sync() {
    var open = anyOverlayOpen();
    if (open === isOpen) return; // only act on a real closed<->open transition
    isOpen = open;
    docEl.classList.toggle('bno-drawer-open', open);
    docEl.dispatchEvent(new CustomEvent('bno:overlay-change', { detail: { open: open } }));
  }

  /*
   * `hidden` is toggled all over the theme (carousels, filters, predictive
   * search), so coalesce bursts into a single derivation per microtask. sync()
   * itself early-returns when the derived state is unchanged, so the observed
   * noise never turns into user-visible work. queueMicrotask (not rAF) so it is
   * not paused while the tab is backgrounded.
   */
  var pending = false;
  function schedule() {
    if (pending) return;
    pending = true;
    queueMicrotask(function () {
      pending = false;
      sync();
    });
  }

  new MutationObserver(schedule).observe(docEl, {
    subtree: true,
    attributes: true,
    attributeFilter: ['open', 'scroll-lock', 'hidden'],
  });

  // Cover the (unlikely) case of an overlay already open at script load.
  sync();
})();
